'use client';

import { useCallback } from 'react';
import { useLanguage, type Language } from '../stores/languageStore';

type InterpolationValues = Record<string, string | number>;

type TranslationsMap<T> = {
  en: T;
  ru: T;
  uz: T;
};

export function useLocalTranslation<T extends Record<string, string>>(
  translations: TranslationsMap<T>
) {
  // Clamp to a supported language. `useLanguage()` is typed `Language`, but a runtime value
  // can slip outside en/ru/uz (server-injected cookie, an out-of-type store write), and
  // consumers index per-language maps by this — e.g. `LOADING_PHRASES[lang]` — so an unknown
  // code gave `undefined.length` and crashed the whole tree. Guarantee en/ru/uz here.
  const raw = useLanguage();
  const lang: Language = raw === 'ru' || raw === 'uz' ? raw : 'en';

  const currentTranslations = translations[lang];

  const t = useCallback(
    <K extends keyof T>(key: K, params?: InterpolationValues): string => {
      const value = currentTranslations[key];

      if (typeof value !== 'string') {
        console.warn(`Translation value is not a string: ${String(key)}`);
        return String(key);
      }

      if (params) {
        return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) =>
          String(params[paramKey] ?? `{{${paramKey}}}`)
        );
      }

      return value;
    },
    [currentTranslations]
  );

  return { t, lang };
}
